import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button, Input, Spinner } from "reactstrap";
import { PhoneCall, ShieldOff } from "react-feather";
import Cookies from "universal-cookie";
import toast, { Toaster } from "react-hot-toast";
import {
  useGetMyEmployeeProfileQuery,
  useUpdateMyEmergencyContactMutation,
} from "../../service";
import { setUserInfo, setUserToken } from "../../redux/authSlice";
import xavaLogo from "../../assets/images/xava-logo.png";

const cookies = new Cookies();

// Hard gate for employees: until HR has called and verified the emergency
// contact on file, the portal is closed. The employee can still correct the
// number from here (which sends HR a fresh verification request) or log out.
// Renders nothing while the profile is loading or once verified.
function EmergencyContactGate({ children }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Poll so the screen opens by itself shortly after HR clicks verify.
  const { data, isLoading } = useGetMyEmployeeProfileQuery(undefined, {
    pollingInterval: 30000,
  });
  const [updateNumber, resUpdate] = useUpdateMyEmergencyContactMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const profile = data?.data;
  const status = profile?.emergencyContactStatus;
  const verified = status === "verified";

  // Don't flash the gate before we know; missing profile means onboarding
  // hasn't created the record yet, and the onboarding gate handles that.
  if (isLoading || !profile || verified) return children;

  const rejected = status === "rejected";

  const save = async () => {
    try {
      const r = await updateNumber({ emergencycontact: draft.trim() }).unwrap();
      toast.success(r?.message || "Number updated. HR will call to verify it.");
      setEditing(false);
      setDraft("");
    } catch (e) {
      toast.error(e?.data?.message || "Could not update the number");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    cookies.remove("MMCL");
    cookies.remove("MMCL_user");
    cookies.remove("isLoggedIn");
    dispatch(setUserInfo({}));
    dispatch(setUserToken(""));
    navigate("/");
  };

  return (
    <div className="xv-emergency-gate">
      <Toaster />
      <img src={xavaLogo} alt="Xava Web Services" />
      <span className="xv-emergency-gate-icon">
        {rejected ? <ShieldOff size={26} /> : <PhoneCall size={26} />}
      </span>
      <h1>
        {rejected
          ? "We could not verify your emergency contact"
          : "Get your emergency contact verified"}
      </h1>
      {rejected ? (
        <p>
          HR called <strong>{profile.emergencycontact || "the number on file"}</strong> but
          could not confirm it as your emergency contact. Please enter the correct
          number below. Your portal opens once HR verifies the new number.
        </p>
      ) : (
        <p>
          We have <strong>{profile.emergencycontact || "no number"}</strong> on file as your
          emergency contact. HR will call this number to confirm it is genuine.
          Your portal opens as soon as it is verified.
        </p>
      )}

      {editing || rejected ? (
        <div className="xv-emergency-gate-form">
          <Input
            type="tel"
            placeholder="Correct emergency contact number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button color="primary" onClick={save} disabled={resUpdate.isLoading || !draft.trim()}>
            {resUpdate.isLoading ? <Spinner size="sm" /> : "Submit for verification"}
          </Button>
        </div>
      ) : (
        <button type="button" className="xv-emergency-gate-link" onClick={() => setEditing(true)}>
          Wrong number? Update it
        </button>
      )}

      <button type="button" className="xv-emergency-gate-logout" onClick={handleLogout}>
        Log out
      </button>
    </div>
  );
}

export default EmergencyContactGate;
